import * as Sentry from '@sentry/vue'
import { nextTick } from 'vue'
import PresentationApi from '@/api/presentation'
import { scrollToActiveSlide } from '@/utils/dom.utils'
import EventBus from '@/eventBus'
import {
  processCanvasBlocksForSlides,
  determineActiveSlide,
  preserveAnnotationProperties,
  validatePresentationId,
  findFirstSlideWithAnnotation,
} from '@/business-logic/data-third-party-action'

/**
 * Updates presentation state with new slides data while preserving annotation properties
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Array} processedSlides - Slides with canvas blocks processed
 * @param {Array} originalSlides - Original slides before update to preserve annotation properties
 */
const updatePresentationState = async (dispatch, processedSlides, originalSlides = []) => {
  await dispatch('setIsNewPresentationAction', false, { root: true })

  // Preserve annotation properties from original slides
  const slidesWithPreservedAnnotations = preserveAnnotationProperties(processedSlides, originalSlides)

  await dispatch('onlyPatchPresentationAction', { slides: slidesWithPreservedAnnotations }, { root: true })
}

/**
 * Sets the active slide in the store
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to set as active
 */
const setActiveSlideInStore = async (dispatch, slideToActivate) => {
  await dispatch('setSlideActive', slideToActivate, { root: true })

  // Emit content sync events for supported slide types
  if (slideToActivate && slideToActivate.slideType) {
    await dispatch('slideContentSync/handleSlideContentChange', slideToActivate, { root: true })
  }
}

/**
 * Emits slide update event to the event bus
 * @param {Object} slideToActivate - The slide that was activated
 */
const emitSlideUpdateEvent = (slideToActivate) => {
  EventBus.$emit('updateNextSlide', slideToActivate.id)
}

/**
 * Clears annotations for the activated slide
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} slideToActivate - The slide to clear annotations for
 */
const clearSlideAnnotations = async (dispatch, slideToActivate) => {
  await dispatch('slideAnnotations/clearAnnotationAction', slideToActivate.id, { root: true })
}

/**
 * Sets the active slide and handles related side effects
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 */
const setActiveSlideIfNeeded = async (dispatch, slideToActivate) => {
  if (!slideToActivate) {
    return
  }

  // Set the active slide in the store
  await setActiveSlideInStore(dispatch, slideToActivate)

  // Emit event for slide update
  emitSlideUpdateEvent(slideToActivate)

  // Clear annotations for the new active slide
  await clearSlideAnnotations(dispatch, slideToActivate)
}

/**
 * Handles DOM updates after slide activation
 */
const performDOMUpdates = async () => {
  await nextTick()
  scrollToActiveSlide()
}

/**
 * Handles the final UI updates after presentation changes
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object|null} slideToActivate - The slide to set as active
 */
const handleUIUpdates = async (dispatch, slideToActivate) => {
  // Handle slide activation and related side effects
  await setActiveSlideIfNeeded(dispatch, slideToActivate)

  // Perform DOM updates
  await performDOMUpdates()
}

/**
 * Fetches updated presentation data from API
 * @param {string} presentationId - The presentation ID to fetch
 * @returns {Promise<Array>} Array of slides from API response
 */
const fetchUpdatedPresentationData = async (presentationId) => {
  const response = await PresentationApi.getPresentationDetails(presentationId)
  return response?.body?.Slides || []
}

/**
 * Updates slide annotations if the feature is enabled
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Function} rootGetters - Vuex root getters function
 * @param {boolean} canUseSlideAnnotations - Whether slide annotations feature is enabled
 * @param {Array} slides - Array of presentation slides
 * @returns {Promise<string|null>} First slide ID with annotation, or null
 */
const updateSlideAnnotations = async (dispatch, rootGetters, canUseSlideAnnotations, slides) => {
  if (!canUseSlideAnnotations) {
    return null
  }

  await dispatch('slideAnnotations/fetchAnnotationsAction', null, { root: true })

  // Get fresh annotations after fetch to avoid stale data
  const annotationsBySlide = rootGetters['slideAnnotations/annotationsBySlide']

  return findFirstSlideWithAnnotation(slides, annotationsBySlide)
}

const actions = {
  /**
   * Handles socket events for presentation updates after third-party slide generation
   * Updates the presentation data and sets the active slide appropriately
   *
   * @param {Object} context - Vuex action context
   * @param {Function} context.dispatch - Vuex dispatch function
   * @param {Object} context.rootState - Root Vuex state
   */
  async handleUpdatePresentationToSocketAction({ dispatch, rootState, rootGetters }) {
    const { presentation } = rootState
    const originalSlides = JSON.parse(JSON.stringify(presentation?.slides || [])) // Deep copy to preserve original state
    const presentationId = presentation?.id

    // Early validation
    if (!validatePresentationId(presentationId)) {
      return
    }

    try {
      // Step 1: Fetch updated presentation data
      const slidesFromApi = await fetchUpdatedPresentationData(presentationId)

      // Step 2: Process slides to fetch canvas blocks
      const processedSlides = await processCanvasBlocksForSlides(slidesFromApi)

      // Step 3: Update presentation state with processed slides
      await updatePresentationState(dispatch, processedSlides, originalSlides)

      // Step 4: Update slide annotations if enabled
      const canUseSlideAnnotations = rootGetters['slideAnnotations/canUseSlideAnnotations']
      const firstSlideIdWithAnnotation = await updateSlideAnnotations(
        dispatch,
        rootGetters,
        canUseSlideAnnotations,
        processedSlides,
      )

      // Step 5: Determine and set the appropriate active slide
      const slideToActivate = determineActiveSlide(
        originalSlides,
        processedSlides,
        firstSlideIdWithAnnotation,
      )

      // Step 6: Handle UI updates
      await handleUIUpdates(dispatch, slideToActivate)
    } catch (error) {
      Sentry.captureException(error)
    }
  },
}

export default {
  namespaced: true,
  actions,
}
